import { useState } from 'react';

import {
  Drawer,
  Button,
  Text,
  Icon,
  Flex,
  Stack,
  Grid,
  Skeleton,
  Hidden,
} from '@backyard-ui/core';
import { ChevronRight } from '@backyard-ui/icons';

import { type ItemId } from '@/infra/services/catalog/model';
import { algolia } from '@/infra/tracking/algolia';
import { useSellers } from '@/presenters/hooks/catalog';
import { useProductStore } from '@/presenters/store/catalog/product';
import { formatPrice } from '@/utils/price/formatPrice';

import { MainProduct } from './MainProduct';
import { SellerItem } from './SellerItem';

interface MoreOptionsDrawerProps {
  itemId: ItemId;
}

function MoreOptionsDrawer(props: Readonly<MoreOptionsDrawerProps>) {
  const { itemId } = props;

  const { data, isFetching } = useSellers(itemId);
  const {
    data: shipments,
    isFetched: shipmentsIsFetched,
    isFetching: shipmentsIsFetching,
  } = useProductStore((store) => store.shipments);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  if (isFetching) {
    return <Skeleton height="40px" data-testid="skeleton" />;
  }

  if (!data || data.length <= 3) {
    return null;
  }

  const firstSeller = data[0];
  const price = firstSeller.pricing.price.to;

  return (
    <Drawer.Root
      size="xl"
      isOpen={isDrawerOpen}
      onOpenChange={(prev) => setIsDrawerOpen(prev)}
    >
      <Drawer.Trigger asChild color="red">
        <Button
          radius="sm"
          variant="ghost"
          isFullWidth
          onClick={() => algolia.sendPdpClickEvent()}
        >
          <Flex align="center" justify="space-between" className="w-full">
            <Text
              decoration="underline"
              color="primary-700"
              weight="semibold"
              size="md"
              asChild
            >
              <span>Mais opções a partir de {formatPrice(price)}</span>
            </Text>
            <Icon size="sm">
              <ChevronRight />
            </Icon>
          </Flex>
        </Button>
      </Drawer.Trigger>
      <Drawer.Content>
        <Drawer.CloseButton />
        <Drawer.Title UNSAFE_className="pr-10">
          Compare os preços entre nossas lojas parceiras
        </Drawer.Title>
        <Drawer.Description>
          <Stack spacing="4" direction="column">
            <MainProduct />
            <Hidden below="md">
              <Grid.Root className="w-full" columns={7}>
                <Grid.Item size={{ md: 1 }}>
                  <Text>Lojas</Text>
                </Grid.Item>
                <Grid.Item>
                  <Text>Preço do produto</Text>
                </Grid.Item>
                <Grid.Item size={{ md: 3 }}>
                  <Text>Opções de compra</Text>
                </Grid.Item>
              </Grid.Root>
            </Hidden>
            <Stack direction="column" spacing="8">
              {data.map((seller, i) => (
                <SellerItem
                  key={i}
                  seller={seller}
                  onAddToCart={() => setIsDrawerOpen(false)}
                  shipment={{
                    data: shipments[seller.id],
                    isFetched: shipmentsIsFetched,
                    isFetching: shipmentsIsFetching,
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Drawer.Description>
      </Drawer.Content>
    </Drawer.Root>
  );
}

export default MoreOptionsDrawer;
